import { createFileRoute, Outlet, redirect, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { LoadingScreen } from '@/features/layout/LoadingScreen';

/**
 * Pathless layout for unauthenticated routes (/sign-in).
 *
 * beforeLoad: redirects authenticated + settled users straight to /chat.
 *
 * AuthLayout adds two extra guards so the sign-in form never flashes:
 *   1. isLoading=true  → <LoadingScreen> while MSAL processes handleRedirectPromise.
 *   2. isAuthenticated=true → <LoadingScreen> + imperative navigate to /chat via
 *      useEffect (handles the race where beforeLoad ran before React re-rendered
 *      with the updated auth context).
 */
export const Route = createFileRoute('/_auth')({
  beforeLoad: ({ context }) => {
    if (!context.auth.isLoading && context.auth.isAuthenticated) {
      throw redirect({ to: '/chat', replace: true });
    }
  },
  component: AuthLayout,
});

function AuthLayout() {
  const { auth } = Route.useRouteContext();
  const navigate = useNavigate();

  useEffect(() => {
    if (!auth.isLoading && auth.isAuthenticated) {
      navigate({ to: '/chat', replace: true });
    }
  }, [auth.isLoading, auth.isAuthenticated, navigate]);

  // Never render the sign-in form while MSAL is processing or the user is
  // already authenticated (transition still in flight).
  if (auth.isLoading || auth.isAuthenticated) {
    return <LoadingScreen message='Loading...' />;
  }

  return (
    <div className='min-h-screen flex flex-col bg-background'>
      <Outlet />
    </div>
  );
}
